import {ticketDtoStatusEnum, type TicketDtoStatusEnum} from "@/global/api/gen/ts/TicketDto";
import {z} from "zod";
import {zodRequiredString} from "@/global/types";
import {differenceString} from "@/global/helpers/text";

export const TicketsTypes = {
    'archive-table': 'Корректировка исторических данных',
    technical: 'Технические проблемы',
    study: 'Проблемы с обучением',
    other: 'Прочее'
};

export const TicketStatus: Record<TicketDtoStatusEnum, { color: string, label: string }> = {
    [ticketDtoStatusEnum['1']]: {color: '#FF9999', label: 'Новый'},
    [ticketDtoStatusEnum['2']]: {color: '#B099E5', label: 'Ожидает подтверждения диспетчера'},
    [ticketDtoStatusEnum['3']]: {color: '#F6D899', label: 'Ожидает подтверждения клиента'},
    [ticketDtoStatusEnum['4']]: {color: '#F6D899', label: 'Направлено в КАП МВД РК'},
    [ticketDtoStatusEnum['5']]: {color: '#F6D899', label: 'Направлено в УАП МВД РК'},
    [ticketDtoStatusEnum['10']]: {color: '#B3EDA2', label: 'Закрыт'},
    [ticketDtoStatusEnum['20']]: {color: '#D5D5D5', label: 'Отклонен'},
    [ticketDtoStatusEnum['30']]: {color: '#99C2F2', label: 'Направлено в РГП ИПЦ'}
};

const zodTransform = z.string().transform((arg) => arg.trim());

const iinZodFieldInfo = z.object({
    old: zodTransform,
    new: zodTransform,
}).transform((arg) => {
    if (!arg.new && !arg.old) {
        return undefined;
    }
    return arg;
}).superRefine((arg, ctx) => {
    if (!arg) {
        return true;
    }
    if (!arg.old) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Поле "Некорректное значение" должно быть заполнено, если "Новое значение" заполнено',
            path: ['old'],
        });
        return false;
    }
    if (!arg.new) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Поле "Новое значение" должно быть заполнено, если "Некорректное значение" заполнено',
            path: ['new'],
        });
        return false;
    }
    if (!differenceString(arg.new, arg.old, 2)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Разница между полями, должна быть не более 2 символов',
            path: ['new'],
        });
        return false;
    }
    return true;
}).optional();

const zodFieldInfo = z.object({
    old: zodTransform,
    new: zodTransform,
}).transform((arg) => {
    if (!arg.new && !arg.old) {
        return undefined;
    }
    return arg;
}).superRefine((arg, ctx) => {
    if (!arg) {
        return true;
    }
    if (!arg.old) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Поле "Некорректное значение" должно быть заполнено, если "Новое значение" заполнено',
            path: ['old'],
        });
    }
    if (!arg.new) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Поле "Новое значение" должно быть заполнено, если "Некорректное значение" заполнено',
            path: ['new'],
        });
    }
    return true;
}).optional();

export const zodCreateTicketSchema = z.object({
    title: zodRequiredString,
    message: z.string().min(30, 'Сообщение должно быть длиной более 30 символов'),
    additional_info: z.object({
        iin: iinZodFieldInfo,
        school_bin: zodFieldInfo,
        school_title: zodFieldInfo,
        first_name: zodFieldInfo,
        last_name: zodFieldInfo,
        patronymic: zodFieldInfo,
        category: zodFieldInfo,
        group_title: zodFieldInfo,
        start_date: zodFieldInfo,
        end_date: zodFieldInfo,
        maintenance_grade: zodFieldInfo,
        rules_grade: zodFieldInfo,
        practice_grade: zodFieldInfo
    }).optional()
});

export type zodCreateTicketType = z.infer<typeof zodCreateTicketSchema>;

export type AdditionalInfoKeys =
    | 'iin'
    | 'school_bin'
    | 'school_title'
    | 'first_name'
    | 'last_name'
    | 'patronymic'
    | 'category'
    | 'group_title'
    | 'start_date'
    | 'end_date'
    | 'maintenance_grade'
    | 'rules_grade'
    | 'practice_grade';
